/**
 * One wardrobe bulk import, item by item.
 *
 * A Drive import used to be visible only as three counters per member. This
 * page shows every file it pulled in the same grid a member's wardrobe uses,
 * each with the stage of the wardrobe pipeline it has reached and whether it
 * is queued, running, done, failed or waiting on review - including files
 * that never became a garment, which are the ones an operator is looking for.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet,
  listWardrobeBulkImportItemsApiV1InternalWardrobeBulkImportsImportIdItemsGet,
  memberWardrobeApiV1InternalUsersIdWardrobeGet,
  type WardrobeBulkImportItem,
  type WardrobeBulkImportItemPage,
  type WardrobeBulkImportStatus,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, Section, StatRow, StatTile } from '@ui/console/primitives';
import { Quantity, formatCount } from '@ui/console/measures';
import { EntityRef, IdentifierCopy } from './references';
import { ActionDialog } from '@ui/console/PageShell';
import { SelectField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Actions, DetailList, Filters, Page, Pager, Status } from './components';
import {
  GARMENT_GRID_CLASS,
  GARMENT_TILE_CLASS,
  GarmentDetail,
  InsightState,
  importIsTerminal,
  useInsight,
  when,
  type GarmentWithProducingJob,
} from './consumer-insights';
import { GarmentImage, SignatureExpiryBoundary } from './garment-image';
import { errorDetail } from './operation-policy';
import {
  MemberFailure,
  MemberSelectionOutcome,
  memberUsesSelection,
} from './drive-import-selection';

const PAGE = 60;

export const STAGE_LABEL: Record<string, string> = {
  drive_import: 'Drive transfer',
  classify: 'Classification',
  attributes: 'Attributes',
  digitise: 'Digitisation',
  title_description: 'Title and description',
  embed: 'Embedding',
  finalize: 'Finalise',
  dedup_grouping: 'Dedup grouping',
  deduplication: 'Deduplication',
  attribute_extraction: 'Attribute extraction',
  enhancement: 'Enhancement',
  validation: 'Validation',
  reverse_search: 'Reverse search',
  title_generation: 'Title generation',
  embedding: 'Embedding',
};

export const ITEM_STATES = ['queued', 'running', 'needs_review', 'done', 'failed'] as const;
type ItemState = (typeof ITEM_STATES)[number];

const STATE_LABEL: Record<ItemState, string> = {
  queued: 'Queued',
  running: 'Processing',
  needs_review: 'Needs review',
  done: 'Done',
  failed: 'Failed',
};

function stageLabel(stage: string): string {
  return STAGE_LABEL[stage] ?? stage;
}

function stateLabel(state: string): string {
  return STATE_LABEL[state as ItemState] ?? state;
}

/** Nothing about this import will change on its own any more. */
export function importSettled(
  status: WardrobeBulkImportStatus | undefined,
  items: WardrobeBulkImportItemPage | undefined,
): boolean {
  if (!status || !items) return false;
  const moving = (items.state_counts['queued'] ?? 0) + (items.state_counts['running'] ?? 0);
  return importIsTerminal(status.status) && moving === 0;
}

function StageChip({ item }: { item: WardrobeBulkImportItem }) {
  return (
    <span
      className="flex flex-wrap items-center gap-1.5"
      data-stage={item.stage}
      data-state={item.state}
    >
      <Status value={stateLabel(item.state)} />
      <span className="text-xs text-tertiary">{stageLabel(item.stage)}</span>
    </span>
  );
}

function ItemTile({
  item,
  showMember,
  onOpen,
}: {
  item: WardrobeBulkImportItem;
  /** An import for several members says whose wardrobe each file went to. */
  showMember: boolean;
  onOpen: () => void;
}) {
  const title = item.title?.trim() || item.original_filename;
  return (
    <li className="flex min-w-0 flex-col gap-1">
      <button type="button" aria-haspopup="dialog" onClick={onOpen} className={GARMENT_TILE_CLASS}>
        <GarmentImage
          url={item.image_url}
          refPresent={item.image_ref}
          originalUrl={item.raw_image_url}
          pending={item.state === 'queued' || item.state === 'running'}
          alt={title}
        />
        <span className="truncate text-sm font-medium text-primary" title={title}>
          {title}
        </span>
        <StageChip item={item} />
        {item.state === 'failed' && (item.error_code || item.error_detail) ? (
          <span className="flex min-w-0 flex-col text-xs text-error-primary">
            {item.error_detail ? <span className="break-words">{item.error_detail}</span> : null}
            {item.error_code ? (
              <span className="font-mono break-all opacity-75">{item.error_code}</span>
            ) : null}
          </span>
        ) : null}
        {item.state === 'needs_review' && item.review_reason ? (
          <span className="text-xs text-tertiary">{item.review_reason}</span>
        ) : null}
      </button>
      {showMember ? (
        <span className="min-w-0 px-1 text-xs" data-part="item-member">
          <EntityRef kind="member" id={item.member_id} hideId />
        </span>
      ) : null}
    </li>
  );
}

/** For an item with no garment yet: where it has been, stage by stage. */
function StageHistory({ item, onClose }: { item: WardrobeBulkImportItem; onClose: () => void }) {
  return (
    <ActionDialog
      title={item.original_filename}
      description="This file has not become a garment yet. Its progress through the wardrobe pipeline is below."
      onClose={onClose}
      footer={
        <Button size="sm" color="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <DetailList
          items={[
            ['Where it is', <StageChip item={item} />],
            ['Drive file', <IdentifierCopy label="Drive file id" value={item.drive_file_id} />],
            ...(item.error_code
              ? ([['Error', <FailureText raw={item.error_detail || item.error_code} />]] as Array<
                  [ReactNode, ReactNode]
                >)
              : []),
            ...(item.review_reason
              ? ([['Review reason', item.review_reason]] as Array<[ReactNode, ReactNode]>)
              : []),
          ]}
        />
        {(item.stages ?? []).length ? (
          <ol className="flex flex-col divide-y divide-secondary rounded-xl border border-secondary">
            {(item.stages ?? []).map((stage) => (
              <li
                key={stage.name}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-2"
              >
                <span className="text-sm text-secondary">{stageLabel(stage.name)}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <Status value={stage.status} />
                  {stage.attempt > 0 ? (
                    <span className="text-xs text-tertiary" data-part="stage-attempt">
                      Attempt {formatCount(stage.attempt)}
                      {stage.max_attempts > 0 ? ` of ${formatCount(stage.max_attempts)}` : ''}
                    </span>
                  ) : null}
                  {stage.finished_at || stage.started_at ? (
                    <span className="text-xs text-tertiary">
                      {when(stage.finished_at ?? stage.started_at)}
                    </span>
                  ) : null}
                </span>
                {stage.error_code || stage.error_detail ? (
                  <span className="w-full text-xs">
                    <FailureText raw={stage.error_detail || stage.error_code || undefined} />
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <span className="text-sm text-tertiary">
            {item.stage === 'drive_import'
              ? 'The file never arrived from Drive, so no pipeline stage ran.'
              : 'No pipeline stage has been created for this file yet; it is waiting to be grouped.'}
          </span>
        )}
      </div>
    </ActionDialog>
  );
}

/** The existing garment dialog, for an item that already is a garment. */
function ItemGarment({
  item,
  onClose,
  onFallback,
}: {
  item: WardrobeBulkImportItem;
  onClose: () => void;
  onFallback: () => void;
}) {
  const [garment, setGarment] = useState<GarmentWithProducingJob>();
  const [failure, setFailure] = useState<string>();
  const { member_id: memberId, garment_id: garmentId } = item;
  const load = useCallback(async () => {
    configureAdminApi();
    const result = await memberWardrobeApiV1InternalUsersIdWardrobeGet({
      path: { id: memberId },
      query: { garment_id: garmentId ?? undefined, limit: 1 },
    });
    handleUnauthorized(result.response.status);
    const found = result.data?.items[0];
    if (result.error || !found) {
      setFailure(errorDetail(result.error, 'This garment could not be loaded.'));
      return;
    }
    setGarment(found);
  }, [memberId, garmentId]);
  useEffect(() => {
    void load();
  }, [load]);

  if (failure)
    return (
      <ActionDialog title={item.title || item.original_filename} onClose={onClose}>
        <div className="flex flex-col gap-3">
          <Callout tone="error" title="The garment could not be loaded.">
            <FailureText raw={failure} />
          </Callout>
          <Actions>
            <Button size="sm" color="secondary" onClick={onFallback}>
              Show stage history
            </Button>
          </Actions>
        </div>
      </ActionDialog>
    );
  if (!garment) return null;
  return (
    <GarmentDetail memberId={item.member_id} garment={garment} onClose={onClose} onRefresh={load} />
  );
}

export function WardrobeImportDetail() {
  const params = useParams();
  const importId = params['importId'] ?? '';
  const [search, setSearch] = useSearchParams();
  const stage = search.get('stage') ?? '';
  const state = search.get('state') ?? '';
  const offset = Number(search.get('offset') ?? 0) || 0;
  const [opened, setOpened] = useState<{ item: WardrobeBulkImportItem; history: boolean }>();
  const [settled, setSettled] = useState(false);

  const status = useInsight<WardrobeBulkImportStatus>(
    () =>
      getWardrobeBulkImportApiV1InternalWardrobeBulkImportsImportIdGet({
        path: { import_id: importId },
      }),
    [importId],
    { pauseRefresh: settled },
  );
  const items = useInsight<WardrobeBulkImportItemPage>(
    () =>
      listWardrobeBulkImportItemsApiV1InternalWardrobeBulkImportsImportIdItemsGet({
        path: { import_id: importId },
        query: {
          limit: PAGE,
          offset,
          ...(stage ? { stage: stage as WardrobeBulkImportItem['stage'] } : {}),
          ...(state ? { state: state as ItemState } : {}),
        },
      }),
    [importId, stage, state, offset],
    // Live while anything can still move; still once every item is terminal.
    { pauseRefresh: settled || opened !== undefined },
  );

  const statusData = status.state.status === 'ready' ? status.state.data : undefined;
  const severalMembers = (statusData?.members.length ?? 0) > 1;
  const itemsData = items.state.status === 'ready' ? items.state.data : undefined;
  const stageOptions = Array.from(
    new Set([...(stage ? [stage] : []), ...Object.keys(itemsData?.stage_counts ?? {})]),
  );
  useEffect(() => {
    setSettled(importSettled(statusData, itemsData));
  }, [statusData, itemsData]);

  const write = (key: 'stage' | 'state', value: string) =>
    setSearch((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('offset');
      return next;
    });

  return (
    <Page
      title="Wardrobe import"
      purpose="Use this page to see every file one Drive import brought in, which stage of the wardrobe pipeline each has reached, and which ones failed or are waiting on review."
      actions={
        <Button
          size="md"
          color="secondary"
          onClick={() => {
            void status.refresh();
            void items.refresh();
          }}
        >
          Refresh
        </Button>
      }
    >
      <InsightState source={status} subject="this wardrobe import">
        {(data) => <ImportHeader status={data} counts={itemsData} settled={settled} />}
      </InsightState>
      <Section
        title="Items"
        description="One tile per imported file, in the order Drive returned them. Open a garment to act on it; open a file that is not a garment yet to see its stage history."
      >
        <Filters>
          <SelectField
            label="Stage"
            value={stage}
            onChange={(event) => write('stage', event.currentTarget.value)}
            options={[
              { value: '', label: 'All stages' },
              ...stageOptions.map((value) => ({
                value,
                label: stageLabel(value),
              })),
            ]}
          />
          <SelectField
            label="State"
            value={state}
            onChange={(event) => write('state', event.currentTarget.value)}
            options={[
              { value: '', label: 'All states' },
              ...ITEM_STATES.map((value) => ({ value, label: STATE_LABEL[value] })),
            ]}
          />
        </Filters>
        <InsightState source={items} subject="this import’s items">
          {(page, receivedAt) => (
            <SignatureExpiryBoundary signedAt={receivedAt} onRefresh={() => items.refresh()}>
              {page.items.length ? (
                <ul className={GARMENT_GRID_CLASS} aria-label="Import items">
                  {page.items.map((item) => (
                    <ItemTile
                      key={item.item_id}
                      item={item}
                      showMember={severalMembers}
                      onOpen={() => setOpened({ item, history: !item.garment_id })}
                    />
                  ))}
                </ul>
              ) : (
                <span className="text-sm text-tertiary">
                  {stage || state
                    ? 'No item of this import matches these filters.'
                    : 'This import has not brought in any files yet.'}
                </span>
              )}
              {page.total > page.limit ? (
                <Pager
                  offset={page.offset}
                  limit={page.limit}
                  total={page.total}
                  onPage={(next) =>
                    setSearch((current) => {
                      const params = new URLSearchParams(current);
                      params.set('offset', String(next));
                      return params;
                    })
                  }
                />
              ) : null}
            </SignatureExpiryBoundary>
          )}
        </InsightState>
      </Section>
      {opened?.history ? (
        <StageHistory item={opened.item} onClose={() => setOpened(undefined)} />
      ) : opened ? (
        <ItemGarment
          item={opened.item}
          onClose={() => {
            setOpened(undefined);
            void items.refresh({ preserve: true }).catch(() => undefined);
          }}
          onFallback={() => setOpened({ item: opened.item, history: true })}
        />
      ) : null}
    </Page>
  );
}

function ImportHeader({
  status,
  counts,
  settled,
}: {
  status: WardrobeBulkImportStatus;
  counts: WardrobeBulkImportItemPage | undefined;
  settled: boolean;
}) {
  const detailed = status.members.filter(
    (member) => member.failure_reason || memberUsesSelection(member),
  );
  const unexplained =
    status.status === 'failed' && !status.members.some((member) => member.failure_reason);
  const count = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : formatCount(value);
  return (
    <Section title="Import">
      {unexplained ? (
        <Callout tone="error" title="This import failed.">
          No reason was recorded. Any file that did reach the wardrobe pipeline is listed below with
          its own error.
        </Callout>
      ) : null}
      <DetailList
        items={[
          ['Import reference', <IdentifierCopy label="Import id" value={status.import_id} />],
          [
            status.members.length === 1 ? 'Member' : 'Members',
            <span className="flex flex-col items-start gap-2">
              {status.members.map((member) => (
                <span key={member.member_id} className="flex flex-wrap items-center gap-2">
                  <EntityRef
                    kind="member"
                    id={member.member_id}
                    {...(member.label ? { secondary: member.label } : {})}
                  />
                  <Status value={member.status} />
                  <span className="text-xs text-tertiary" data-part="member-counts">
                    {count(member.images_discovered)} found · {count(member.images_new)} new ·{' '}
                    {count(member.images_duplicate)} already in wardrobe ·{' '}
                    {count(member.images_skipped)} skipped · {count(member.images_failed)} failed
                    {member.images_queued !== 0 ||
                    (member.status !== 'completed' && member.status !== 'failed')
                      ? ` · ${count(member.images_queued)} queued`
                      : ''}{' '}
                    · {count(member.pipelines_started)}{' '}
                    {member.pipelines_started === 1 ? 'pipeline' : 'pipelines'} started
                  </span>
                  <Button
                    size="sm"
                    color="link-color"
                    href={`/consumers/${member.member_id}?tab=drive-import`}
                  >
                    Their Drive imports
                  </Button>
                </span>
              ))}
            </span>,
          ],
          [
            'Source',
            status.drive_folder_link ? (
              <a
                href={status.drive_folder_link}
                target="_blank"
                rel="noreferrer"
                className="text-sm break-all text-brand-secondary underline"
              >
                {status.drive_folder_link}
              </a>
            ) : (
              'A manifest of Drive files, no folder'
            ),
          ],
          [
            'Queued by',
            status.created_by ? <EntityRef kind="staff" id={status.created_by} /> : 'Not recorded',
          ],
          ['Queued', when(status.created_at)],
          ['Finished', when(status.completed_at, 'Not finished yet')],
          [
            'Overall state',
            <span className="flex flex-wrap items-center gap-2">
              <Status value={status.status} />
              {settled ? null : (
                <span className="text-xs text-tertiary">Following live until it finishes.</span>
              )}
            </span>,
          ],
        ]}
      />
      <StatRow>
        {ITEM_STATES.map((value) => {
          // Unknown until the items read answers: a dash, never a false zero.
          const count = counts ? (counts.state_counts[value] ?? 0) : undefined;
          return (
            <StatTile
              key={value}
              label={STATE_LABEL[value]}
              value={
                count === undefined ? (
                  <span className="text-quaternary">—</span>
                ) : (
                  <Quantity value={formatCount(count)} raw={count} />
                )
              }
            />
          );
        })}
      </StatRow>
      {detailed.map((member) => (
        <div
          key={member.member_id}
          className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-4"
        >
          {status.members.length > 1 ? (
            <EntityRef
              kind="member"
              id={member.member_id}
              {...(member.label ? { secondary: member.label } : {})}
            />
          ) : null}
          {member.failure_reason ? <MemberFailure reason={member.failure_reason} /> : null}
          <MemberSelectionOutcome
            member={member}
            foldersHref={`/consumers/${encodeURIComponent(member.member_id)}?tab=folders`}
          />
        </div>
      ))}
    </Section>
  );
}
